import { type DISPLAY_FROM_SHOPFRONT, type DISPLAY_TO_SHOPFRONT } from "../DisplayEvents";

export enum DisplayDriverEvents {
    DRIVER_CLOSE = "DRIVER_CLOSE",
    DRIVER_OPEN = "DRIVER_OPEN",
    MESSAGE = "MESSAGE",
}

export interface DriverMessageData {
    type: DISPLAY_TO_SHOPFRONT;
    body: unknown;
}

export type DriverMessageListener = (data: DriverMessageData) => void;
export type DriverOpenListener = () => void;
export type DriverCloseListener = () => void;

export default abstract class BaseDisplayDriver {
    protected listeners: Record<DisplayDriverEvents, Set<Function>> = {
        [DisplayDriverEvents.DRIVER_CLOSE]: new Set<DriverCloseListener>(),
        [DisplayDriverEvents.DRIVER_OPEN] : new Set<DriverOpenListener>(),
        [DisplayDriverEvents.MESSAGE]     : new Set<DriverMessageListener>(),
    };

    /**
     * Add callback to listen to messages from customer display
     * @param type
     * @param listener
     */
    public addListener(type: DisplayDriverEvents.MESSAGE, listener: DriverMessageListener): void;
    public addListener(type: DisplayDriverEvents.DRIVER_OPEN, listener: DriverOpenListener): void;
    public addListener(type: DisplayDriverEvents.DRIVER_CLOSE, listener: DriverCloseListener): void;
    public addListener(type: DisplayDriverEvents, listener: Function): void {
        if(!(type in this.listeners)) {
            throw new TypeError(`Invalid listener type ("${type}") attempting to be added`);
        }

        this.listeners[type].add(listener);
    }

    /**
     * Remove callback from listening to messages from customer display
     * @param type
     * @param listener
     */
    public removeListener(type: DisplayDriverEvents.MESSAGE, listener: DriverMessageListener): void;
    public removeListener(type: DisplayDriverEvents.DRIVER_OPEN, listener: DriverOpenListener): void;
    public removeListener(type: DisplayDriverEvents.DRIVER_CLOSE, listener: DriverCloseListener): void;
    public removeListener(type: DisplayDriverEvents, listener: Function): void {
        if(!(type in this.listeners)) {
            throw new TypeError(`Invalid listener type ("${type}") attempting to be removed`);
        }

        this.listeners[type].delete(listener);
    }

    /**
     * Emit event from customer display to shopfront
     * @param type
     * @param data
     * @protected
     */
    protected emit(type: DisplayDriverEvents.MESSAGE, data: DriverMessageData): void;
    protected emit(type: DisplayDriverEvents.DRIVER_OPEN | DisplayDriverEvents.DRIVER_CLOSE): void;
    protected emit(type: DisplayDriverEvents, data?: unknown): void {
        if(!(type in this.listeners)) {
            throw new TypeError(`Invalid event type ("${type}") attempting to be emitted`);
        }

        for(const listener of this.listeners[type]) {
            listener(data);
        }
    }

    public abstract init(): Promise<void>;
    public abstract open(): Promise<boolean>;
    public abstract close(): Promise<void>;
    public abstract sendMessage(type: DISPLAY_FROM_SHOPFRONT, body: unknown): Promise<void>;
}
